import React, { useEffect, useState, useRef, useCallback } from "react";
import {
  ChevronDown,
  ChevronUp,
  ChevronLeft,
  ChevronRight,
} from "lucide-react";
import { useNavigate } from "react-router";
import {
  getAllroleplaysForVisibility,
  updateScenarioVisibility,
  getCategories
} from "../../requests";
import { useDispatch } from "react-redux";
import { startLoader, stopLoader } from "../../_store/reducers/loaderReducer";
import styles from "../../_css/RoleplayManagement.module.css";
import CategoryVisibilityModal from "../Modals/CategoryVisibilityModal";
import { notifyError, notifySuccess, getLangCode } from "../../utils/helper.js";

const VisibilityAndCategories = () => {
  const [menuOpenIndex, setMenuOpenIndex] = useState(null);
  const [subcategorieDropDown, setSubcategorieDropDown] = useState(null);
  const [showCategoryDropdown, setShowCategoryDropdown] = useState(false);

  // Initialize with an ID
  const [selectedCategory, setSelectedCategory] = useState("2");

  const [categoryData, setCategoryData] = useState([]);
  const [categories, setCategories] = useState([]);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [itemsPerPage] = useState(10);
  const [showCategoryModal, setShowCategoryModal] = useState(false);

  const navigate = useNavigate();
  const dispatch = useDispatch();

  const categoryDropdownRef = useRef(null);
  const subcategoryDropdownRef = useRef(null);
  const menuDropdownRef = useRef(null);

  const toggleCategoryDropdown = () =>
    setShowCategoryDropdown(!showCategoryDropdown);

  const handleCategorySelect = (categoryId) => {
    setSelectedCategory(categoryId);
    setPage(1);
    setShowCategoryDropdown(false);
  };

  // 1. Fetch Categories
  const fetchCategoriesData = useCallback(async () => {
    try {
      const response = await getCategories();
      if (response?.categories) {
        setCategories(response.categories);
      }
    } catch (error) {
      console.error("Error fetching categories:", error);
    }
  }, []);

  // 2. Fetch Table Data (Roleplays)
  const fetchTableData = useCallback(async () => {
    dispatch(startLoader());
    try {
      const params = {
        category: selectedCategory,
        page,
        limit: itemsPerPage,
      };
      const response = await getAllroleplaysForVisibility(params);
      if (response.data) {
        setCategoryData(response.data);
        if (response.pagination)
          setTotalPages(response.pagination.totalPages);
      }
    } catch (error) {
      console.error("Error fetching data:", error);
      setCategoryData([]);
    } finally {
      dispatch(stopLoader());
    }
  }, [selectedCategory, page, itemsPerPage, dispatch]);


  // Initial load for categories
  useEffect(() => {
    fetchCategoriesData();
  }, [fetchCategoriesData]);

  // Load table data when category or page changes
  useEffect(() => {
    fetchTableData();
  }, [fetchTableData]);

  // Close dropdowns on outside click
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        categoryDropdownRef.current &&
        !categoryDropdownRef.current.contains(event.target)
      )
        setShowCategoryDropdown(false);
      if (
        subcategoryDropdownRef.current &&
        !subcategoryDropdownRef.current.contains(event.target)
      )
        setSubcategorieDropDown(null);
      if (
        menuDropdownRef.current &&
        !menuDropdownRef.current.contains(event.target)
      )
        setMenuOpenIndex(null);
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);



  // Handler passed to the Modal to refresh parent data
  const handleRefreshData = () => {
    fetchCategoriesData();
    fetchTableData();
  };

  const handleVisibilityToggle = async (scenarioId, currentVisibility) => {
    try {
      dispatch(startLoader());

      let newVisibility;
      if (currentVisibility === "Yes") {
        newVisibility = false;
      } else if (currentVisibility === "No") {
        newVisibility = true;
      } else {
        newVisibility = !currentVisibility;
      }

      const res = await updateScenarioVisibility(scenarioId, newVisibility);

      setCategoryData((prevData) =>
        prevData.map((item) =>
          item.id === scenarioId
            ? { ...item, visibility: newVisibility }
            : item
        )
      );

      notifySuccess(res.message || "Visibility updated successfully");
    } catch (error) {
      console.error("Error updating visibility:", error);
      const backendMessage =
        error?.response?.data?.message || "Failed to update visibility";
      notifyError(backendMessage);
    } finally {
      dispatch(stopLoader());
    }
  };

  const getSelectedLabel = () => {
    if (selectedCategory === "All") return "All";
    const cat = categories.find((c) => c.value == selectedCategory);
    return cat ? cat.label : "Select Category";
  };

  const handlePageChange = (newPage) => {
    if (newPage >= 1 && newPage <= totalPages) setPage(newPage);
  };

  const filteredData = categoryData.filter((item) => {
    if (selectedCategory === "All") return true;
    const selectedLabel = getSelectedLabel();
    return item.category === selectedLabel;
  });

  return (
    <div>
      <div className={styles.container}>
        {/* header */}
        <div className={styles.header}>
          {/* Left: Category Dropdown */}
          <div
            className={styles.categoryDropdownWrapper}
            ref={categoryDropdownRef}
          >
            <button
              onClick={toggleCategoryDropdown}
              className={styles.categoryButton}
            >
              {getSelectedLabel()}
              {showCategoryDropdown ? (
                <ChevronUp size={16} />
              ) : (
                <ChevronDown size={16} />
              )}
            </button>

            {showCategoryDropdown && (
              <div className={styles.categoryDropdown}>
                {categories.map((item, idx) => (
                  <div
                    key={idx}
                    onClick={() => handleCategorySelect(item.value)}
                    className={`${styles.categoryDropdownItem} ${item.value == selectedCategory
                      ? styles.categoryDropdownItemActive
                      : ""
                      }`}
                  >
                    {item.label} ({getLangCode(item.language)})
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Right: Manage Category Button */}
          <button
            className={styles.manageCategoryButton}
            onClick={() => setShowCategoryModal(true)}
          >
            Manage Category
          </button>
        </div>

        {/* Table Container */}
        <div className={styles.tableContainer}>
          <table className={styles.table}>
            <thead>
              <tr>
                <th
                  className={`${styles.tableHeader} ${styles.tableHeaderFirst}`}
                >
                  <div className={styles.tableHeaderContent}>
                    <input type="checkbox" className={styles.checkbox} />
                    <span className={styles.tableHeaderText}>S.L</span>
                  </div>
                </th>
                {[
                  { label: "Category", width: "80px" },
                  { label: "Sub-Category", width: "200px" },
                  { label: "Title of Scenario", width: "200px" },
                  { label: "Visibility", width: "90px" },
                ].map((col, idx, array) => (
                  <th
                    key={idx}
                    className={`${styles.tableHeader} ${idx === array.length - 1 ? styles.tableHeaderLast : ""
                      }`}
                    style={{ minWidth: col.width }}
                  >
                    {col.label}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {filteredData.length > 0 ? (
                filteredData.map((row, index) => {
                  const isLastRow = index === filteredData.length - 1;
                  return (
                    <tr key={index} className={styles.tableRow}>
                      <td
                        className={`${styles.tableCell} ${styles.tableCellFirst}`}
                        style={{
                          borderBottomLeftRadius: isLastRow ? "12px" : "0",
                        }}
                      >
                        <div className={styles.tableCellContent}>
                          <input type="checkbox" className={styles.checkbox} />
                          <span className={styles.tableCellText}>
                            {row.sl}
                          </span>
                        </div>
                      </td>

                      <td className={styles.tableCell}>
                        <span className={styles.tableCellText}>
                          {row.category}
                        </span>
                      </td>

                      <td className={styles.tableCell}>
                        <span className={styles.tableCellText}>
                          {row.title}
                        </span>
                      </td>

                      <td className={styles.tableCell}>
                        <span className={styles.tableCellText}>
                          {row.scenerio_title}
                        </span>
                      </td>
                      <td
                        className={`${styles.tableCell} ${styles.tableCellCenter}`}
                      >
                        <div className="d-flex justify-content-start align-items-center">
                          <label className="switch">
                            <input
                              type="checkbox"
                              checked={
                                row.visibility === true ||
                                row.visibility === "Yes"
                              }
                              onChange={() =>
                                handleVisibilityToggle(row.id, row.visibility)
                              }
                            />
                            <span className="slider round"></span>
                          </label>
                        </div>
                      </td>
                    </tr>
                  );
                })
              ) : (
                <tr>
                  <td
                    colSpan="5"
                    className={styles.emptyState}
                    style={{ textAlign: "center", padding: "20px" }}
                  >
                    No roleplays found.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
        <div className={styles.paginationContainer}>
          {totalPages > 1 && (
            <div className={styles.paginationWrapper}>
              <button
                disabled={page === 1}
                onClick={() => handlePageChange(page - 1)}
                className={styles.paginationButton}
              >
                <ChevronLeft size={16} /> Previous
              </button>

              <div className={styles.paginationNumbers}>
                {[...Array(totalPages)].map((_, idx) => {
                  const pageNum = idx + 1;
                  const isActive = pageNum === page;
                  return (
                    <button
                      key={pageNum}
                      onClick={() => handlePageChange(pageNum)}
                      className={`${styles.paginationNumber} ${isActive ? styles.paginationNumberActive : ""
                        }`}
                    >
                      {pageNum.toString().padStart(2, "0")}
                    </button>
                  );
                })}
              </div>

              <button
                disabled={page === totalPages}
                onClick={() => handlePageChange(page + 1)}
                className={`${styles.paginationButton} ${styles.paginationButtonNext}`}
              >
                Next <ChevronRight size={16} />
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Passed handleRefreshData to the modal */}
      <CategoryVisibilityModal
        show={showCategoryModal}
        onHide={() => setShowCategoryModal(false)}
        onCategoryUpdate={handleRefreshData}
      />
    </div>
  );
};

export default VisibilityAndCategories;